// Incremental compile pipeline with selector-level block cache reuse.
// Unchanged blocks with unchanged dependencies keep previous rendered CSS.
import { shouldApplyClassVariableTransform } from '../index-transform'
import { resolveLuisImports } from '../imports'
import { normalizeSourceWithDiagnostics } from '../normalize'
import { parseLuisIncremental } from '../parser/incremental'
import { preprocessLuisSource } from '../preprocess/extract-and-expand-loops'
import { collectRenderLeaves } from '../renderer'
import type { LuisCompileOptions, LuisCompileResult } from '../types'
import { transformSourceWithClassVariables } from '../variables/class-variable'
import { enforceIncrementalLeafBudgets, prepareIncrementalLeaves } from './compile-leaves'
import { buildColdStartCacheOutput, buildIncrementalCompileResult, buildWarmCacheOutput } from './compile-output'
import { buildIncrementalReuseArtifacts } from './compile-reuse'
import type { LuisIncrementalCompileOutput } from './compile-types'
import { profileLuisSource, resolveCompileMode } from '../profile'
import { resolveResourceLimits, resolveResourcePolicy } from '../resource-limits'
import type { LuisIncrementalCompileCache } from './types'
import { rethrowMappedCompileError } from '../error-remap'

// Compile .luis source with incremental selector-block reuse.
export function compileLuisWithIncrementalCache(
  source: string,
  options: LuisCompileOptions = {},
  previousCache?: LuisIncrementalCompileCache,
): LuisIncrementalCompileOutput {
  const collectWarnings = options.collectWarnings !== false
  const resourcePolicy = resolveResourcePolicy(options)
  const resourceLimits = resolveResourceLimits(options)
  const sourceId = options.sourceId ?? '<inline>'
  const importResolution = resolveLuisImports(source, options)
  try {
    const { source: normalized, warnings: normalizeWarnings } = normalizeSourceWithDiagnostics(importResolution.source, collectWarnings)
    const sourceProfile = profileLuisSource(normalized)
    const resolvedMode = resolveCompileMode(sourceProfile, options.compileMode)
    const { source: preprocessedSource, variables, classVariables, objectVariables, arrayVariables, warnings: preprocessWarnings } =
      preprocessLuisSource(normalized, collectWarnings, resolvedMode.mode, {
        sourceId,
        resourcePolicy,
        resourceLimits,
      })
    const sourceWithClassTransforms = shouldApplyClassVariableTransform(
      resolvedMode.mode,
      classVariables,
      objectVariables,
      arrayVariables,
    )
      ? transformSourceWithClassVariables(preprocessedSource, {
          classVariables,
          variables,
          objectVariables,
          arrayVariables,
        })
      : preprocessedSource
    const parsed = parseLuisIncremental(sourceWithClassTransforms, objectVariables, arrayVariables, previousCache?.parseCache)
    const renderWarnings: LuisCompileResult['warnings'] = []
    const leaves = collectRenderLeaves(parsed.root, renderWarnings)
    const resourceWarnings: LuisCompileResult['warnings'] = []
    const budgetedLeaves = enforceIncrementalLeafBudgets(leaves, sourceId, resourcePolicy, resourceLimits, resourceWarnings)
    const preparedLeaves = prepareIncrementalLeaves(budgetedLeaves)
    const debugEnabled = options.debug === true
    const lineOffset = options.lineOffset ?? 0
  // Check whether the compile can use the cold-start fast path.
    if (!previousCache && !debugEnabled) {
      const result = buildIncrementalCompileResult({
        sourceId,
        resourcePolicy,
        resourceLimits,
        rootDeclarations: parsed.root.declarations,
        collectWarnings,
        importWarnings: importResolution.warnings,
        normalizeWarnings,
        preprocessWarnings,
        renderWarnings,
        resourceWarnings,
        variables,
        classVariables,
        objectVariables,
        arrayVariables,
        lineOffset,
        debugEnabled,
        preparedLeaves,
      })
      // Return cold-start output when cache reuse is not needed.
      return buildColdStartCacheOutput(
        result,
        resolvedMode.mode,
        preparedLeaves,
        parsed.cache,
        parsed.stats,
      )
    }
    const reuse = buildIncrementalReuseArtifacts(
      preparedLeaves.allLeaves,
      previousCache,
      variables,
      classVariables,
      objectVariables,
      arrayVariables,
      sourceId,
      lineOffset,
      debugEnabled,
    )
    const result = buildIncrementalCompileResult({
      sourceId,
      resourcePolicy,
      resourceLimits,
      rootDeclarations: parsed.root.declarations,
      collectWarnings,
      importWarnings: importResolution.warnings,
      normalizeWarnings,
      preprocessWarnings,
      renderWarnings,
      resourceWarnings,
      variables,
      classVariables,
      objectVariables,
      arrayVariables,
      lineOffset,
      debugEnabled,
      ruleOverridesByKey: reuse.ruleOverridesByKey,
      preparedLeaves,
    })
    // Return warm incremental output with cache reuse artifacts.
    return buildWarmCacheOutput(
      result,
      resolvedMode.mode,
      preparedLeaves,
      reuse.nextBlocks,
      parsed.cache,
      reuse.reusedBlocks,
      parsed.stats,
    )
  } catch (error) {
    rethrowMappedCompileError(error, importResolution.lineMap, sourceId)
  }
}
